import { getSupabaseAnonKey, getSupabaseClient, getSupabaseUrl } from '@/api/supabaseClient';
import { uploadImage } from '@/api/contentService';

const LOGS_STORAGE_KEY = 'mbv_local_logs_v1';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isNativePlatform = () => {
  if (typeof window === 'undefined') return false;
  if (typeof window.Capacitor?.isNativePlatform === 'function') {
    return window.Capacitor.isNativePlatform();
  }
  return false;
};

const getNativePlatform = () => {
  if (!isNativePlatform()) return 'web';
  return String(window.Capacitor?.getPlatform?.() || '').toLowerCase() || 'native';
};

const openOAuthUrl = async (url) => {
  if (!url || typeof window === 'undefined') return;

  const browserPlugin = window.Capacitor?.Plugins?.Browser;
  if (browserPlugin?.open) {
    await browserPlugin.open({ url });
    return;
  }

  const popup = window.open(url, '_blank', 'noopener,noreferrer');
  if (!popup) {
    window.location.href = url;
  }
};

const nowIso = () => new Date().toISOString();

const valuesEqual = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

const parseSort = (sortBy) => {
  if (!sortBy || typeof sortBy !== 'string') return { field: null, desc: false };
  const desc = sortBy.startsWith('-');
  return { field: desc ? sortBy.slice(1) : sortBy, desc };
};

const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'pt-BR', { numeric: true, sensitivity: 'base' });
};

const sortRows = (rows, sortBy) => {
  const { field, desc } = parseSort(sortBy);
  if (!field) return rows;
  return [...rows].sort((left, right) => {
    const result = compareValues(left[field], right[field]);
    return desc ? -result : result;
  });
};

const matchesOperator = (actual, expected) => {
  if (expected == null || typeof expected !== 'object' || Array.isArray(expected)) {
    return valuesEqual(actual, expected);
  }

  if ('$gte' in expected) return String(actual ?? '') >= String(expected.$gte ?? '');
  if ('$lte' in expected) return String(actual ?? '') <= String(expected.$lte ?? '');
  if ('$gt' in expected) return String(actual ?? '') > String(expected.$gt ?? '');
  if ('$lt' in expected) return String(actual ?? '') < String(expected.$lt ?? '');
  if ('$ne' in expected) return !valuesEqual(actual, expected.$ne);
  if ('$in' in expected && Array.isArray(expected.$in)) {
    return expected.$in.some((candidate) => valuesEqual(actual, candidate));
  }

  return JSON.stringify(actual) === JSON.stringify(expected);
};

const matchesCriteria = (row, criteria = {}) => {
  const entries = Object.entries(criteria);
  if (entries.length === 0) return true;

  return entries.every(([key, expected]) => {
    const actual = row[key];
    if (Array.isArray(expected)) return expected.some((candidate) => valuesEqual(actual, candidate));
    return matchesOperator(actual, expected);
  });
};

const mapAuthUser = (authUser) => {
  if (!authUser) return null;
  const fallbackName = authUser.email ? authUser.email.split('@')[0] : 'Usuario';
  return {
    id: authUser.id,
    email: authUser.email || '',
    full_name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || fallbackName,
    role: authUser.app_metadata?.role || 'member',
  };
};

const isTruthy = (value) => value === true || String(value || '').toLowerCase() === 'true';

function normalizeEmailAddress(value) {
  return String(value || '').trim().toLowerCase();
}

function isValidEmailAddress(value) {
  const email = normalizeEmailAddress(value);
  if (!email || email.length > 254) return false;
  if (!EMAIL_REGEX.test(email)) return false;
  if (email.includes('..')) return false;

  const parts = email.split('@');
  if (parts.length !== 2) return false;
  const [localPart, domain] = parts;
  if (!localPart || !domain || localPart.length > 64) return false;
  if (domain.startsWith('.') || domain.endsWith('.')) return false;
  return true;
}

function assertValidEmailAddress(value, contextLabel = 'email') {
  if (!isValidEmailAddress(value)) {
    throw new Error(`Endereco de e-mail invalido para ${contextLabel}.`);
  }
}

const MEMBER_ALLOWED_STATUS = new Set(['Ativo', 'Pendente', 'Rejeitado']);

function normalizeMemberPayload(payload = {}, { forUpdate = false } = {}) {
  const next = { ...payload };
  const nullableFields = [
    'cpf',
    'birth_date',
    'phone',
    'gender',
    'marital_status',
    'parent_name',
    'parent_phone',
    'cep',
    'address',
    'address_number',
    'complement',
    'neighborhood',
    'city',
    'state',
    'congregation',
    'membership_date',
    'baptism_date',
    'group_id',
    'photo_url',
    'observations',
  ];

  nullableFields.forEach((field) => {
    if (next[field] === '') next[field] = null;
  });

  if (next.email != null) {
    const normalizedEmail = String(next.email).trim().toLowerCase();
    next.email = normalizedEmail || null;
  }

  if (next.age === '') next.age = null;
  if (next.age != null && next.age !== '') {
    const parsedAge = Number(next.age);
    next.age = Number.isFinite(parsedAge) ? parsedAge : null;
  }

  if (next.status != null && !MEMBER_ALLOWED_STATUS.has(next.status)) {
    if (forUpdate) {
      delete next.status;
    } else {
      next.status = 'Pendente';
    }
  }

  return next;
}

const ENTITY_TABLE_MAP = {
  Video: 'videos',
  Album: 'albums',
  Banner: 'banners',
  ChurchInfo: 'church_infos',
  Event: 'events',
  WorshipService: 'worship_services',
  ServicePlan: 'service_plans',
  AppDownload: 'app_downloads',
  Ministry: 'ministries',
  Member: 'members',
  Leader: 'leaders',
  Group: 'groups',
  WorshipTeamMember: 'worship_team_members',
  MediaTeamMember: 'media_team_members',
  DanceTeamMember: 'dance_team_members',
  ReceptionTeamMember: 'reception_team_members',
  KidsMBVMember: 'kids_mbv_members',
  WorshipSchedule: 'worship_schedules',
  MediaSchedule: 'media_schedules',
  DanceSchedule: 'dance_schedules',
  ReceptionSchedule: 'reception_schedules',
  KidsMBVSchedule: 'kids_mbv_schedules',
  ScheduleRole: 'schedule_roles',
  AvailabilityBlock: 'availability_blocks',
  MediaAvailabilityBlock: 'media_availability_blocks',
  DanceAvailabilityBlock: 'dance_availability_blocks',
  ReceptionAvailabilityBlock: 'reception_availability_blocks',
  KidsMBVAvailabilityBlock: 'kids_mbv_availability_blocks',
  GroupEvent: 'group_events',
  Message: 'messages',
  Notification: 'notifications',
  Review: 'reviews',
  Song: 'songs',
  PrayerRequest: 'prayer_requests',
  Role: 'roles',
  UserRole: 'user_roles',
  AdminPermission: 'admin_permissions',
  RankingMBVCheckin: 'ranking_mbv_checkins',
  RankingMBVAdjustment: 'ranking_mbv_adjustments',
  RankingMBVQrCode: 'ranking_mbv_qr_codes',
  RankingMBVSetting: 'ranking_mbv_settings',
  RankingMBVAdminLog: 'ranking_mbv_admin_logs',
};

const STRUCTURED_TABLES = new Set(['videos', 'albums', 'banners', 'members']);

function ensureEntityTable(entityName) {
  const table = ENTITY_TABLE_MAP[entityName];
  if (!table) throw new Error(`Entity not mapped: ${entityName}`);
  return table;
}

function flattenRow(table, row) {
  if (!row) return row;
  if (STRUCTURED_TABLES.has(table)) return row;
  return {
    id: row.id,
    created_date: row.created_date,
    updated_date: row.updated_date,
    ...(row.data || {}),
  };
}

function normalizePayload(payload = {}) {
  const next = { ...payload };
  delete next.id;
  delete next.created_date;
  delete next.updated_date;
  return next;
}

async function listRows(entityName) {
  const supabase = getSupabaseClient();
  const table = ensureEntityTable(entityName);
  const selectColumns = STRUCTURED_TABLES.has(table) ? '*' : 'id, created_date, updated_date, data';
  const { data, error } = await supabase
    .from(table)
    .select(selectColumns)
    .limit(2000);
  if (error) throw new Error(`${entityName} list failed: ${error.message}`);
  return (data || []).map((row) => flattenRow(table, row));
}

const createEntityApi = (entityName) => ({
  async list(sortBy, limit = 100) {
    const rows = await listRows(entityName);
    return sortRows(rows, sortBy).slice(0, limit);
  },

  async filter(criteria = {}, sortBy, limit = 100) {
    if (typeof sortBy === 'number') {
      limit = sortBy;
      sortBy = undefined;
    }
    const rows = await listRows(entityName);
    const filtered = rows.filter((row) => matchesCriteria(row, criteria));
    return sortRows(filtered, sortBy).slice(0, limit);
  },

  async create(data = {}) {
    const supabase = getSupabaseClient();
    const table = ensureEntityTable(entityName);
    const payload = entityName === 'Member'
      ? normalizeMemberPayload(data)
      : { ...data };

    if (entityName === 'Notification' && payload.is_read === undefined) {
      payload.is_read = false;
    }

    if (STRUCTURED_TABLES.has(table)) {
      const { data: created, error } = await supabase
        .from(table)
        .insert(payload)
        .select('*')
        .single();
      if (error) throw new Error(`${entityName} create failed: ${error.message}`);
      return created;
    }

    const row = {
      id: payload.id,
      created_date: payload.created_date || nowIso(),
      updated_date: nowIso(),
      data: normalizePayload(payload),
    };

    const { data: created, error } = await supabase
      .from(table)
      .insert(row)
      .select('id, created_date, updated_date, data')
      .single();
    if (error) throw new Error(`${entityName} create failed: ${error.message}`);
    return flattenRow(table, created);
  },

  async update(id, data = {}) {
    const supabase = getSupabaseClient();
    const table = ensureEntityTable(entityName);

    if (STRUCTURED_TABLES.has(table)) {
      const normalizedData = entityName === 'Member'
        ? normalizeMemberPayload(data, { forUpdate: true })
        : data;

      const { data: updated, error } = await supabase
        .from(table)
        .update(normalizedData)
        .eq('id', id)
        .select('*')
        .maybeSingle();
      if (error) throw new Error(`${entityName} update failed: ${error.message}`);
      if (!updated) throw new Error(`${entityName} update failed: registro nao encontrado ou sem permissao.`);
      return updated;
    }

    const { data: current, error: currentError } = await supabase
      .from(table)
      .select('id, created_date, updated_date, data')
      .eq('id', id)
      .single();
    if (currentError) throw new Error(`${entityName} update failed: ${currentError.message}`);

    const mergedData = {
      ...(current?.data || {}),
      ...normalizePayload(data),
    };

    const { data: updated, error } = await supabase
      .from(table)
      .update({
        data: mergedData,
        updated_date: nowIso(),
      })
      .eq('id', id)
      .select('id, created_date, updated_date, data')
      .maybeSingle();
    if (error) throw new Error(`${entityName} update failed: ${error.message}`);
    return flattenRow(table, updated || {
      id,
      created_date: current.created_date,
      updated_date: nowIso(),
      data: mergedData,
    });
  },

  async delete(id) {
    const supabase = getSupabaseClient();
    const table = ensureEntityTable(entityName);
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (error) throw new Error(`${entityName} delete failed: ${error.message}`);
    return { success: true, id };
  },
});

const entities = new Proxy({}, {
  get(_target, prop) {
    return createEntityApi(String(prop));
  },
});

const auth = {
  async me() {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.getUser();
    if (error || !data?.user) {
      const error = new Error('Authentication required');
      error.status = 401;
      throw error;
    }

    const mappedUser = mapAuthUser(data.user);
    if (!mappedUser?.email) return mappedUser;

    try {
      const { data: permissionRows, error: permissionError } = await supabase
        .from('admin_permissions')
        .select('id, data')
        .limit(500);

      if (!permissionError && Array.isArray(permissionRows)) {
        const normalizedEmail = mappedUser.email.trim().toLowerCase();
        const userId = String(mappedUser.id || '');

        const isSuperAdmin = permissionRows.some((row) => {
          const data = row?.data || {};
          const permissionEmail = String(data.user_email || data.email || '').trim().toLowerCase();
          const permissionUserId = String(data.user_id || '');
          const matchesUser = permissionEmail === normalizedEmail || (permissionUserId && permissionUserId === userId);
          return matchesUser && isTruthy(data.is_super_admin);
        });

        if (isSuperAdmin) {
          mappedUser.role = 'admin';
        }
      }
    } catch (_err) {
      // keep default role when permission lookup fails
    }

    return mappedUser;
  },

  async isAuthenticated() {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.getSession();
    if (error) return false;
    return !!data?.session;
  },

  async signIn(email, password) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw new Error(error.message);
    return mapAuthUser(data?.user);
  },

  async signUp(email, password, redirectUrl) {
    assertValidEmailAddress(email, 'cadastro');
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.signUp({
      email: normalizeEmailAddress(email),
      password,
      options: {
        emailRedirectTo: redirectUrl || (typeof window !== 'undefined' ? window.location.href : undefined),
      },
    });
    if (error) throw new Error(error.message);
    return mapAuthUser(data?.user);
  },

  async signInWithOAuth(provider, redirectUrl) {
    const supabase = getSupabaseClient();
    const providerScopes =
      provider === 'apple'
        ? 'name email'
        : undefined;
    const nativePlatform = getNativePlatform();
    const shouldHandleRedirectManually = nativePlatform !== 'web';
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirectUrl || (typeof window !== 'undefined' ? window.location.href : undefined),
        scopes: providerScopes,
        skipBrowserRedirect: shouldHandleRedirectManually,
      },
    });
    if (error) throw new Error(error.message);

    if (shouldHandleRedirectManually && data?.url) {
      await openOAuthUrl(data.url);
    }

    return data;
  },

  async resetPassword(email, redirectUrl) {
    assertValidEmailAddress(email, 'recuperacao de senha');
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.resetPasswordForEmail(normalizeEmailAddress(email), {
      redirectTo: redirectUrl || (typeof window !== 'undefined' ? window.location.href : undefined),
    });
    if (error) throw new Error(error.message);
    return { success: true };
  },

  async updatePassword(password) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.updateUser({ password });
    if (error) throw new Error(error.message);
    return data;
  },

  async logout(redirectUrl) {
    const supabase = getSupabaseClient();
    await supabase.auth.signOut();
    if (redirectUrl && typeof window !== 'undefined') window.location.href = redirectUrl;
  },

  redirectToLogin(redirectUrl) {
    if (redirectUrl && typeof window !== 'undefined') window.location.href = redirectUrl;
  },
};

const integrations = {
  Core: {
    async UploadFile({ file }) {
      const file_url = await uploadImage(file, 'uploads');
      return { file_url };
    },

    async SendEmail(payload = {}) {
      assertValidEmailAddress(payload?.to, 'envio de e-mail');
      const normalizedPayload = {
        ...payload,
        text: payload?.text || payload?.message || undefined,
      };
      const supabase = getSupabaseClient();
      const invokeWithSession = async (accessToken) => supabase.functions.invoke('send-email', {
        body: normalizedPayload,
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || null;
      let { data, error } = await invokeWithSession(accessToken);

      if (error && /401|unauthorized/i.test(String(error.message || ''))) {
        const { data: refreshedData } = await supabase.auth.refreshSession();
        const refreshedToken = refreshedData?.session?.access_token || null;
        ({ data, error } = await invokeWithSession(refreshedToken));
      }

      if (error) throw new Error(`SendEmail failed: ${error.message}`);
      if (data?.success === false) {
        const detail = typeof data.error === 'string' ? data.error : JSON.stringify(data.error || data);
        throw new Error(`SendEmail failed: ${detail}`);
      }
      return data || { success: true, provider: 'supabase-edge-function' };
    },

    async SendPushNotification(payload = {}) {
      const supabase = getSupabaseClient();
      const invokeWithSession = async (accessToken) => supabase.functions.invoke('send-push', {
        body: payload,
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || null;
      let { data, error } = await invokeWithSession(accessToken);

      if (error && /401|unauthorized/i.test(String(error.message || ''))) {
        const { data: refreshedData } = await supabase.auth.refreshSession();
        const refreshedToken = refreshedData?.session?.access_token || null;
        ({ data, error } = await invokeWithSession(refreshedToken));
      }

      if (error) throw new Error(`SendPushNotification failed: ${error.message}`);
      return data || { success: true, provider: 'supabase-edge-function' };
    },
  },
};

async function resolveEdgeFunctionAccessToken(supabase) {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (!userError && userData?.user) {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token || null;
    if (accessToken) {
      return accessToken;
    }
  }

  const { data: refreshedData, error: refreshError } = await supabase.auth.refreshSession();
  if (refreshError) {
    throw new Error(refreshError.message || 'Falha ao renovar a sessao.');
  }

  const refreshedToken = refreshedData?.session?.access_token || null;
  if (!refreshedToken) {
    throw new Error('Sessao expirada. Faca login novamente.');
  }

  return refreshedToken;
}

async function invokeEdgeFunctionWithFetch(name, payload, accessToken) {
  const response = await fetch(`${getSupabaseUrl()}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: getSupabaseAnonKey(),
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload || {}),
  });

  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  const isJson = contentType.includes('application/json');
  const responseBody = isJson
    ? await response.json().catch(() => null)
    : await response.text().catch(() => '');

  if (!response.ok) {
    const message =
      responseBody?.error ||
      responseBody?.message ||
      responseBody?.msg ||
      responseBody?.details ||
      (typeof responseBody === 'string' ? responseBody : '') ||
      `HTTP ${response.status}`;

    const error = new Error(`${name} failed: ${message}`);
    error.status = response.status;
    throw error;
  }

  return { data: responseBody || { success: true } };
}

const functions = {
  async invoke(name, payload = {}) {
    const supabase = getSupabaseClient();
    const accessToken = await resolveEdgeFunctionAccessToken(supabase);

    try {
      return await invokeEdgeFunctionWithFetch(name, payload, accessToken);
    } catch (error) {
      const shouldRetryWithRefresh = /401|unauthorized|invalid jwt|jwt expired|jwt/i.test(
        String(error?.message || '').toLowerCase(),
      );

      if (!shouldRetryWithRefresh) {
        throw error;
      }

      const { data: refreshedData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        throw new Error(refreshError.message || 'Falha ao renovar a sessao.');
      }

      const refreshedToken = refreshedData?.session?.access_token || null;
      if (!refreshedToken) {
        throw new Error('Sessao expirada. Faca login novamente.');
      }

      return await invokeEdgeFunctionWithFetch(name, payload, refreshedToken);
    }
  },
};

const appLogs = {
  async logUserInApp(pageName) {
    if (typeof window === 'undefined') return { success: true };
    const raw = window.localStorage.getItem(LOGS_STORAGE_KEY);
    const logs = raw ? JSON.parse(raw) : [];
    logs.push({ pageName, timestamp: nowIso() });
    window.localStorage.setItem(LOGS_STORAGE_KEY, JSON.stringify(logs.slice(-200)));
    return { success: true };
  },
};

export const base44 = {
  auth,
  entities,
  integrations,
  functions,
  appLogs,
};
